import React, { useEffect, useState } from 'react'
import PropTypes from 'prop-types'
import convert from 'htmr'
import useLocale from '../../hooks/useLocale'
import cn from 'classnames'
import DatoImage from '../../components/DatoRichText/DatoImage'
import useBreakpoints from '../../hooks/useBreakpoints'
import PremiumButton from '../../components/PremiumButton/PremiumButton'
import Cross from 'media/cross.svg?svgr-webpack'
import ChevronLeft from 'media/chevron-left-green.svg?svgr-webpack'
import { FormattedMessage } from 'react-intl'

export default function RecipeViewer({
  recipe: {
    recipeName: name,
    intro,
    time,
    image,
    instructions,
    ingredients,
    ...rest
  },
  onClose,
  showNutrientSection = true,
  showSignUpButton = true,
}) {
  useEffect(() => {
    const bodyStyle = document.body.style
    const close = () => {
      onClose()
    }
    const checkForEsc = (e) => {
      const ESC = 27
      if (e.keyCode === ESC) close()
    }

    // Prevent scrolling
    bodyStyle.top = `-${window.scrollY}px`
    bodyStyle.position = 'fixed'
    bodyStyle.width = '100vw'
    bodyStyle.paddingRight = '15px'

    window.addEventListener('keyup', checkForEsc)
    return () => {
      // Re-enable scrolling
      const scrollY = bodyStyle.top
      bodyStyle.position = ''
      bodyStyle.top = ''
      bodyStyle.width = ''
      bodyStyle.paddingRight = ''
      window.scrollTo(0, parseInt(scrollY || '0', 10) * -1)

      window.removeEventListener('keyup', checkForEsc)
    }
  }, [onClose])

  return (
    <>
      <section className="fixed right-0 top-0 z-40 h-full w-21/24 bg-Bg md:w-2/3 lg:w-1/3 xxl:w-1/4">
        <CloseButton onClick={onClose} />
        <div className="h-full overflow-x-scroll">
          <DatoImage
            lazy={false}
            src={`${image.url}&w=600&h=450`}
            className="w-full"
            alt=""
            width={600}
            height={450}
          />
          <div className="p-16 lg:p-24">
            <h1 className="type-med-plus-fat mb-6">{name}</h1>
            <TimeAndEnergy time={time} calories={rest.calories} />
            {intro && (
              <div className="type-med-minus mb-24 leading-body">
                {convert(intro)}
              </div>
            )}
            <Ingredients
              ingredients={ingredients}
              switchOrder={rest.ruSwitchIngredientOrder}
            />
            <Instructions instructions={instructions} />
            {showNutrientSection && <NutritionalInfo {...rest} />}
            {showSignUpButton && (
              <>
                <p className="type-default-fat mx-auto -mb-24 w-4/5 text-center">
                  <FormattedMessage
                    id="recipes_cta_heading"
                    defaultMessage="Discover hundreds of tasty and nutritious recipes in our app"
                  />
                </p>
                <PremiumButton small GALabel={`Recipe: ${name}`} />
              </>
            )}
          </div>
        </div>
      </section>
      <div
        className="fixed inset-0 z-30 bg-black bg-opacity-50"
        onClick={onClose}
      />
    </>
  )
}

RecipeViewer.propTypes = {
  recipe: PropTypes.object.isRequired,
  onClose: PropTypes.func.isRequired,
  showNutrientSection: PropTypes.bool,
  showSignUpButton: PropTypes.bool,
}

function Expander({ length, children }) {
  const [expanded, setExpanded] = useState(false)
  if (length < 3) return children
  return (
    <div className="relative" onClick={() => !expanded && setExpanded(true)}>
      {!expanded && (
        <div
          className="absolute bottom-0 left-0 right-0 z-10"
          style={{
            height: 50,
            background:
              'linear-gradient(to bottom, rgba(255, 255, 255, 0), rgba(255, 255, 255, 1))',
          }}
        />
      )}
      <button
        className="absolute left-0 w-full cursor-pointer bg-Bg pt-12 text-center"
        style={{ top: '100%' }}
        onClick={(e) => {
          e.stopPropagation()
          setExpanded(!expanded)
        }}
      >
        <span className="sr-only">{expanded ? 'Collapse' : 'Expand'}</span>
        <ChevronLeft
          className={cn('transform', expanded ? 'rotate-90' : '-rotate-90')}
        />
      </button>
      <div
        className={cn({
          'overflow-hidden': !expanded,
        })}
        style={!expanded && { height: 110 }}
      >
        {children}
      </div>
    </div>
  )
}

Expander.propTypes = {
  children: PropTypes.any.isRequired,
  length: PropTypes.number.isRequired,
}

function CloseButton({ onClick }) {
  return (
    <div className="absolute right-0 top-0 z-20">
      <button
        onClick={() => onClick()}
        className={cn(
          'rounded-full border-1 border-Border bg-Bg shadow-elevated',
          'mr-24 mt-24',
          'flex justify-center',
          'cursor-pointer',
        )}
        style={{ width: 48, height: 48 }}
      >
        <Cross className="self-center stroke-current text-Brand" />
        <span className="sr-only">
          <FormattedMessage id="redeem_page_close" defaultMessage="Close" />
        </span>
      </button>
    </div>
  )
}

CloseButton.propTypes = {
  onClick: PropTypes.func,
}

function TimeAndEnergy({ time, calories }) {
  const formattedTime = () => {
    const hours = Math.floor(time / 60)
    const mins = Math.floor(time - hours * 60)

    if (hours) return `${hours} h ${mins} min`
    return `${mins} min`
  }

  return (
    <div className="mb-12 flex text-TypeSub">
      {time && (
        <p>
          <span className="sr-only">
            <FormattedMessage id="recipes_time" defaultMessage="Time" />:{' '}
          </span>
          {formattedTime()}
        </p>
      )}
      {time && calories && (
        <span className="mx-6 inline-block" aria-hidden="true">
          ·
        </span>
      )}
      {calories && (
        <p>
          <span className="sr-only">
            <FormattedMessage
              id="recipes_per_serving"
              defaultMessage="Per serving"
            />
            :{' '}
          </span>
          {calories} kcal
        </p>
      )}
    </div>
  )
}

TimeAndEnergy.propTypes = {
  time: PropTypes.number,
  calories: PropTypes.number,
}

function Ingredients({ ingredients, switchOrder }) {
  const { country, language } = useLocale()
  if (!ingredients.length) return null
  const formattedIngredient = ({
    ingredientMetric,
    ingredientName,
    ingredientUs,
  }) => {
    const amount =
      country === 'US' && ingredientUs.length ? ingredientUs : ingredientMetric
    return switchOrder && language === 'ru'
      ? `${ingredientName}: ${amount}`
      : `${amount} ${ingredientName}`
  }

  return (
    <div className="mb-64">
      <h2 className="type-upper-fat mb-16">
        <FormattedMessage
          id="recipes_ingredients"
          defaultMessage="Ingredients"
        />
      </h2>
      <Expander length={ingredients.length}>
        <ul className="pl-16">
          {ingredients.map((ingredient) => {
            return (
              <li
                className="mb-12 list-disc leading-body text-Brand"
                key={ingredient.id}
              >
                <div className="text-Type">
                  {formattedIngredient(ingredient)}
                </div>
              </li>
            )
          })}
        </ul>
      </Expander>
    </div>
  )
}

Ingredients.propTypes = {
  ingredients: PropTypes.array,
  switchOrder: PropTypes.bool,
}

function Instructions({ instructions }) {
  const { LG } = useBreakpoints()
  if (!instructions.length) return null
  return (
    <div className="mb-64">
      <h2 className="type-upper-fat mb-16">
        <FormattedMessage
          id="recipes_instructions"
          defaultMessage="Instructions"
        />
      </h2>
      <Expander length={instructions.length}>
        <ol>
          {instructions.map(({ instruction, id }, i) => {
            return (
              <li className="mb-12 flex leading-body" key={id}>
                <div
                  className={cn(
                    'flex shrink-0 content-center justify-center',
                    'rounded-full border-1 border-Brand text-Brand',
                    'mr-8',
                  )}
                  aria-hidden="true"
                  style={{
                    width: LG ? 26 : 22,
                    height: LG ? 26 : 22,
                  }}
                >
                  <span className="relative" style={{ bottom: 1 }}>
                    {i + 1}
                  </span>
                </div>
                {convert(instruction)}
              </li>
            )
          })}
        </ol>
      </Expander>
    </div>
  )
}

Instructions.propTypes = {
  instructions: PropTypes.array,
}

function NutritionalInfo({
  calories,
  carbs,
  fiber,
  sugar,
  fat,
  unsaturatedFat,
  saturatedFat,
  protein,
  cholesterol,
  sodium,
  potassium,
}) {
  return (
    <div className="mb-32">
      <h2 className="type-upper-fat mb-2">
        <FormattedMessage
          id="recipes_nutritional_info"
          defaultMessage="Nutritional information"
        />
      </h2>
      <p className="type-small mb-16 text-TypeSub">
        <FormattedMessage
          id="recipes_per_serving"
          defaultMessage="Per serving"
        />
      </p>
      <ul>
        <NutritionalInfoItem
          value={calories}
          label={
            <FormattedMessage id="recipes_calories" defaultMessage="Calories" />
          }
          unit="kcal"
        />
        <NutritionalInfoItem
          value={protein}
          label={
            <FormattedMessage id="recipes_protein" defaultMessage="Protein" />
          }
          unit="g"
        />
        <NutritionalInfoItem
          value={carbs}
          label={<FormattedMessage id="recipes_carbs" defaultMessage="Carbs" />}
          unit="g"
        >
          {(fiber || sugar) && (
            <ul>
              <NutritionalInfoItem
                value={fiber}
                label={
                  <FormattedMessage
                    id="recipes_fibers"
                    defaultMessage="Fibers"
                  />
                }
                unit="g"
                subItem
              />
              <NutritionalInfoItem
                value={sugar}
                label={
                  <FormattedMessage
                    id="recipes_sugars"
                    defaultMessage="Sugars"
                  />
                }
                unit="g"
                subItem
              />
            </ul>
          )}
        </NutritionalInfoItem>
        <NutritionalInfoItem
          value={fat}
          label={<FormattedMessage id="recipes_fat" defaultMessage="Fat" />}
          unit="g"
        >
          {(unsaturatedFat || saturatedFat) && (
            <ul>
              <NutritionalInfoItem
                value={saturatedFat}
                label={
                  <FormattedMessage
                    id="recipes_saturated_fat"
                    defaultMessage="Saturated fat"
                  />
                }
                unit="g"
                subItem
              />
              <NutritionalInfoItem
                value={unsaturatedFat}
                label={
                  <FormattedMessage
                    id="recipes_unsaturated_fat"
                    defaultMessage="Unsaturated fat"
                  />
                }
                unit="g"
                subItem
              />
            </ul>
          )}
        </NutritionalInfoItem>
        <li className="type-default-fat pt-12">
          <FormattedMessage id="recipes_other" defaultMessage="Other" />
          <ul>
            <NutritionalInfoItem
              value={cholesterol}
              label={
                <FormattedMessage
                  id="recipes_cholesterol"
                  defaultMessage="Cholesterol"
                />
              }
              unit="mg"
              subItem
            />
            <NutritionalInfoItem
              value={sodium}
              label={
                <FormattedMessage id="recipes_sodium" defaultMessage="Sodium" />
              }
              unit="mg"
              subItem
            />
            <NutritionalInfoItem
              value={potassium}
              label={
                <FormattedMessage
                  id="recipes_potassium"
                  defaultMessage="Potassium"
                />
              }
              unit="mg"
              subItem
            />
          </ul>
        </li>
      </ul>
    </div>
  )
}

NutritionalInfo.propTypes = {
  calories: PropTypes.number,
  carbs: PropTypes.number,
  fiber: PropTypes.number,
  sugar: PropTypes.number,
  fat: PropTypes.number,
  unsaturatedFat: PropTypes.number,
  saturatedFat: PropTypes.number,
  protein: PropTypes.number,
  cholesterol: PropTypes.number,
  sodium: PropTypes.number,
  potassium: PropTypes.number,
}

function NutritionalInfoItem({
  value,
  label,
  subItem = false,
  unit,
  children,
}) {
  // value can be 0 so !value wouldn’t work
  if (typeof value !== 'number') return

  return (
    <li
      className={cn(
        subItem
          ? 'type-small pb-4 first:pt-6'
          : 'type-default-fat border-b-1 border-Border pb-11 pt-12',
      )}
    >
      <span className="flex">
        <span className="mr-auto">
          {label}
          <span className="sr-only">:</span>
        </span>
        <span>{`${value} ${unit}`}</span>
      </span>
      {children}
    </li>
  )
}

NutritionalInfoItem.propTypes = {
  value: PropTypes.number,
  label: PropTypes.oneOfType([PropTypes.string, PropTypes.object]).isRequired,
  subItem: PropTypes.bool,
  unit: PropTypes.string.isRequired,
  children: PropTypes.any,
}
